<template>
    <v-dialog :value="value" max-width="560" scrollable @input="(open) => !open && close()">
        <v-card class="ui-card">
            <div class="ui-card__header">
                <div>
                    <h3 class="ui-section-title">{{ done ? "Incluído no roteiro" : "Incluir em roteiro" }}</h3>
                    <p class="ui-section-description">{{ description }}</p>
                </div>
            </div>

            <div v-if="done" class="ui-card__body">
                <p class="mb-0">
                    <strong>{{ plural(done.added, "caso incluído", "casos incluídos") }}</strong> em <strong>{{ done.trip.name }}</strong><span v-if="done.team"> ({{ done.team.name }})</span>.
                    <span v-if="done.skipped" class="ui-muted">{{ plural(done.skipped, "já estava", "já estavam") }} nele.</span>
                </p>
                <p class="ui-small ui-muted mt-2 mb-0">No roteiro você ajusta a equipe, a ordem e os dias; a agenda sai em "Gerar agendas".</p>
            </div>

            <div v-else class="ui-card__body">
                <div v-if="loading" class="ptp-loading"><v-progress-circular indeterminate size="22" width="2" color="primary"></v-progress-circular></div>
                <template v-else>
                    <button v-for="trip in trips" :key="trip.id" type="button" class="ptp-option" :class="{ 'ptp-option--active': choice === trip.id }" @click="choice = trip.id">
                        <span class="ptp-option__head">
                            <strong>{{ trip.name }}</strong>
                            <ui-badge set="prospect-trip" :status="trip.status" />
                        </span>
                        <span class="ui-small ui-muted">
                            {{ trip.base ? `${trip.base.name}/${trip.base.uf}` : "sem base" }}<span v-if="trip.start_date"> · saída {{ formatFeedDate(trip.start_date) }}</span>
                            · {{ plural(trip.cases, "caso", "casos") }} · {{ trip.teams.map((team) => team.name).join(", ") }}
                        </span>
                    </button>
                    <p v-if="!trips.length" class="ui-small ui-muted">Nenhum roteiro aberto. Crie um novo abaixo.</p>
                    <button type="button" class="ptp-option" :class="{ 'ptp-option--active': choice === 'new' }" @click="choice = 'new'">
                        <span class="ptp-option__head"><strong><v-icon small left>mdi-plus</v-icon>Novo roteiro</strong></span>
                        <span class="ui-small ui-muted">Com {{ caseIds.length === 1 ? "este caso" : `estes ${caseIds.length} casos` }}{{ uf ? ` (${uf})` : "" }}; a base e o jeito de chegar você define no roteiro.</span>
                    </button>
                    <v-text-field v-if="choice === 'new'" v-model="name" label="Nome do roteiro (opcional)" outlined dense hide-details class="mt-2"></v-text-field>
                </template>
            </div>

            <div class="ui-card__footer">
                <template v-if="done">
                    <v-btn outlined @click="close">Fechar</v-btn>
                    <v-btn color="primary" depressed @click="openTrip"><v-icon left small>mdi-map-marker-path</v-icon>Abrir roteiro</v-btn>
                </template>
                <template v-else>
                    <v-btn outlined :disabled="saving" @click="close">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :loading="saving" :disabled="!choice || !caseIds.length" @click="submit">Incluir</v-btn>
                </template>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import { formatFeedDate, notifyError } from "@/components/prospect/prospect.util";

const OPEN = ["rascunho", "planejado", "em_andamento"];

// "Incluir em roteiro" para um ou mais casos (ficha do caso, lista de sexta):
// escolhe um roteiro aberto ou cria um novo. Emite "done" com o roteiro.
export default {
    name: "ProspectTripPicker",

    props: {
        value: { type: Boolean, default: false }, // aberto (v-model)
        caseIds: { type: Array, default: () => [] },
        uf: { type: String, default: null },
    },

    data: () => ({ trips: [], loading: false, saving: false, choice: null, name: "", done: null }),

    computed: {
        description() {
            if (this.done) return "Pronto. O caso já aparece no mapa e na agenda do roteiro.";
            const count = this.caseIds.length;
            return `${count === 1 ? "O caso entra" : `Os ${count} casos entram`} na primeira equipe do roteiro, no ponto da rota que menos aumenta o caminho.`;
        },
    },

    watch: {
        value(open) {
            if (open) this.reset();
        },
    },

    created() {
        if (this.value) this.reset();
    },

    methods: {
        formatFeedDate,

        plural(count, one, many) {
            return `${count} ${count === 1 ? one : many}`;
        },

        async reset() {
            this.done = null;
            this.choice = null;
            this.name = "";
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/prospect/trips");
                // Roteiros da mesma UF primeiro.
                this.trips = result.data.content.filter((trip) => OPEN.includes(trip.status))
                    .sort((a, b) => Number(b.uf === this.uf) - Number(a.uf === this.uf));
                // Sugere o roteiro da mesma UF; sem UF conhecida, o primeiro aberto; senao, um novo.
                const sameUf = this.uf ? this.trips.find((trip) => trip.uf === this.uf) : this.trips[0];
                this.choice = sameUf ? sameUf.id : "new";
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        async submit() {
            this.saving = true;
            try {
                if (this.choice === "new") {
                    const result = await axios.post("/api/admin/prospect/trips", { case_ids: this.caseIds, uf: this.uf || undefined, name: this.name || undefined });
                    const trip = result.data.content;
                    this.done = { trip: { id: trip.id, name: trip.name }, team: trip.teams[0] || null, added: this.caseIds.length, skipped: 0 };
                } else {
                    const result = await axios.post(`/api/admin/prospect/trips/${this.choice}/cases`, { case_ids: this.caseIds });
                    this.done = result.data.content;
                }
                this.$emit("done", this.done.trip);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.saving = false;
            }
        },

        openTrip() {
            const id = this.done.trip.id;
            this.close();
            this.$router.push(`/prospeccao/rota/${id}`);
        },

        close() {
            this.$emit("input", false);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.ptp-loading {
    display: flex;
    justify-content: center;
    padding: 20px 0;
}

.ptp-option {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    margin-bottom: 8px;
    padding: 10px 12px;
    text-align: left;
    font-size: 13px;
    border: 1px solid $ui-border;
    border-radius: $ui-radius-sm;
    background: #ffffff;
    color: $ui-fg;
    cursor: pointer;

    &:hover {
        border-color: #c4b5fd;
    }
}

.ptp-option--active {
    border-color: #7857a8;
    box-shadow: 0 0 0 1px #7857a8;
    background: #faf5ff;
}

.ptp-option__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}
</style>
